/**
 * Deterministic integer PRNG.
 *
 * `Math.random` is unseeded and useless here: every player on a given date must
 * generate a byte-identical track, and any past day must be reproducible forever
 * for practice mode and ghost replays. Both routines below use only Math.imul
 * and bitwise ops, which ECMAScript specifies exactly, so results match across
 * every engine.
 */

/** FNV-1a, 32-bit. Turns a date string into a seed. */
export function fnv1a(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface Rng {
  /** Uniform in [0, 1). */
  next(): number;
  /** Uniform in [lo, hi). */
  range(lo: number, hi: number): number;
  /** Integer in [lo, hi). */
  int(lo: number, hi: number): number;
}

/** mulberry32 — small, fast, good enough distribution for level generation. */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  const next = (): number => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    range: (lo, hi) => lo + next() * (hi - lo),
    int: (lo, hi) => lo + Math.floor(next() * (hi - lo)),
  };
}

/** UTC date key, e.g. "2026-07-30". The daily track's identity. */
export function dateKey(d: Date = new Date()): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function seedForDate(key: string): number {
  return fnv1a(`karting-hotlap:${key}`);
}

/** Milliseconds until the next UTC midnight, for the "next track in" countdown. */
export function msUntilNextTrack(now: Date = new Date()): number {
  const next = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0,
    0,
    0,
    0,
  );
  return next - now.getTime();
}
